/**
 * Listing — public pages for a published board and the products built on it
 * (marketplace stage 1b).
 *
 *   /product/:provider/:board            the board page: shared gallery, platform
 *                                        description, board specs, product cards,
 *                                        shared documents
 *   /product/:provider/:board/:variant   one product (SKU): its own gallery, its
 *                                        description, board specs + product specs,
 *                                        its documents plus the shared ones
 *
 * Everything comes from one anonymous catalog call. Prices are deliberately
 * not shown (open question in the plan).
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconArrowLeft, IconFileTypePdf, IconExternalLink } from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';
import { getPublicListing, publicFileUrl, publicListingPath, formatBytes, VARIANT_STATUS_LABELS } from '../../services/listings';
import type { PublicListing, PublicVariant, BoardAsset, SpecRow } from '../../services/listings';

const NAVY = '#0f2740';

const Container = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing(6)};
`;

const Section = styled.section`
  padding: ${({ theme }) => theme.spacing(12)} 0;
`;

const Crumb = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.font.color.secondary};
  text-decoration: none;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  &:hover { color: ${NAVY}; }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: ${({ theme }) => theme.spacing(10)};
  align-items: start;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const MainImage = styled.div`
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const Thumbs = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const Thumb = styled.button<{ active: boolean }>`
  width: 72px;
  height: 56px;
  padding: 0;
  border-radius: ${({ theme }) => theme.border.radius.sm};
  border: 2px solid ${({ active }) => (active ? NAVY : 'transparent')};
  background: ${({ theme }) => theme.background.secondary};
  overflow: hidden;
  cursor: pointer;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.font.color.tertiary};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.h1`
  font-size: 40px;
  line-height: 1.1;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  color: ${NAVY};
`;

const Headline = styled.p`
  font-size: 20px;
  line-height: 1.4;
  color: ${({ theme }) => theme.font.color.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
`;

const Facts = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 8px 20px;
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: 14px;
  dt { color: ${({ theme }) => theme.font.color.tertiary}; }
  dd { margin: 0; color: ${({ theme }) => theme.font.color.primary}; }
`;

const Prose = styled.div`
  font-size: 16px;
  line-height: 1.65;
  color: ${({ theme }) => theme.font.color.secondary};
  p { margin: 0 0 ${({ theme }) => theme.spacing(4)}; }
`;

const H2 = styled.h2`
  font-size: 26px;
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  color: ${NAVY};
`;

const SpecGroups = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(6)};
`;

const SpecGroup = styled.div`
  h3 {
    font-size: 13px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.font.color.tertiary};
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
  }
  dl { margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; font-size: 14px; }
  dt { color: ${({ theme }) => theme.font.color.secondary}; }
  dd { margin: 0; color: ${({ theme }) => theme.font.color.primary}; }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${({ theme }) => theme.spacing(5)};
`;

const ProductCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  background: ${({ theme }) => theme.background.primary};
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s, transform 0.15s;
  &:hover { border-color: ${NAVY}; transform: translateY(-2px); }

  .thumb {
    aspect-ratio: 4 / 3;
    border-radius: ${({ theme }) => theme.border.radius.md};
    background: ${({ theme }) => theme.background.secondary};
    display: flex; align-items: center; justify-content: center; overflow: hidden;
    color: ${NAVY}; font-weight: 600;
    img { width: 100%; height: 100%; object-fit: contain; }
  }
  h3 { margin: 0; font-size: 18px; color: ${({ theme }) => theme.font.color.primary}; }
  p { margin: 0; font-size: 14px; line-height: 1.5; color: ${({ theme }) => theme.font.color.secondary}; }
  small { color: ${({ theme }) => theme.font.color.tertiary}; font-size: 12px; }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Item = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(3, 4)};
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.md};
  background: ${({ theme }) => theme.background.primary};
  font-size: 14px;
  a { color: ${NAVY}; text-decoration: none; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
  a:hover { text-decoration: underline; }
  small { color: ${({ theme }) => theme.font.color.tertiary}; }
`;

const Pill = styled.span<{ tone: 'available' | 'coming-soon' | 'discontinued' }>`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ tone }) => (tone === 'available' ? '#e6f4ea' : tone === 'coming-soon' ? '#e8f0fe' : '#f1f3f4')};
  color: ${({ tone }) => (tone === 'available' ? '#137333' : tone === 'coming-soon' ? '#1a56db' : '#5f6368')};
`;

const Muted = styled.p`
  color: ${({ theme }) => theme.font.color.tertiary};
  font-size: 14px;
`;

function groupSpecs(specs: SpecRow[]): { group: string; rows: SpecRow[] }[] {
  const order: string[] = [];
  const map = new Map<string, SpecRow[]>();
  for (const row of specs) {
    const g = row.group || 'Specifications';
    if (!map.has(g)) { map.set(g, []); order.push(g); }
    map.get(g)!.push(row);
  }
  return order.map((g) => ({ group: g, rows: map.get(g)! }));
}

function Paragraphs({ text }: { text: string }) {
  return <Prose>{text.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}</Prose>;
}

function Gallery({ images, heroId, alt }: { images: BoardAsset[]; heroId: number | null; alt: string }) {
  const ordered = [...images].sort((a, b) => (a.id === heroId ? -1 : b.id === heroId ? 1 : 0));
  const [activeId, setActiveId] = useState<number | null>(ordered[0]?.id ?? null);
  const active = ordered.find((a) => a.id === activeId) || ordered[0];
  return (
    <div>
      <MainImage>
        {active ? <img src={publicFileUrl(active.id)} alt={active.title || alt} /> : <Muted>No image</Muted>}
      </MainImage>
      {ordered.length > 1 && (
        <Thumbs>
          {ordered.map((a) => (
            <Thumb key={a.id} active={a.id === active?.id} onClick={() => setActiveId(a.id)} title={a.title || a.file_name}>
              <img src={publicFileUrl(a.id)} alt="" />
            </Thumb>
          ))}
        </Thumbs>
      )}
      {active?.title && <Muted>{active.title}</Muted>}
    </div>
  );
}

function Specs({ specs, title = 'Specifications' }: { specs: SpecRow[]; title?: string }) {
  const groups = groupSpecs(specs);
  if (!groups.length) return null;
  return (
    <Section style={{ paddingTop: 0 }}>
      <Container>
        <H2>{title}</H2>
        <SpecGroups>
          {groups.map((g) => (
            <SpecGroup key={g.group}>
              <h3>{g.group}</h3>
              <dl>
                {g.rows.map((r, i) => <div key={i} style={{ display: 'contents' }}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}
              </dl>
            </SpecGroup>
          ))}
        </SpecGroups>
      </Container>
    </Section>
  );
}

function Documents({ documents, title = 'Documents' }: { documents: BoardAsset[]; title?: string }) {
  if (!documents.length) return null;
  return (
    <Section style={{ paddingTop: 0 }}>
      <Container>
        <H2>{title}</H2>
        <List>
          {documents.map((d) => (
            <Item key={d.id}>
              <a href={publicFileUrl(d.id)} target="_blank" rel="noreferrer">
                <IconFileTypePdf size={18} /> {d.title || d.file_name} <IconExternalLink size={14} />
              </a>
              <small>{formatBytes(d.size_bytes)}</small>
            </Item>
          ))}
        </List>
      </Container>
    </Section>
  );
}

/* ── board page ── */

function BoardPage({ listing }: { listing: PublicListing }) {
  const shared = listing.assets.filter((a) => !a.variant_id);
  const images = shared.filter((a) => a.kind === 'image');
  const documents = shared.filter((a) => a.kind === 'document');
  const heroOf = (v: PublicVariant) => v.hero_asset_id
    ?? listing.assets.find((a) => a.kind === 'image' && a.variant_id === v.id)?.id
    ?? listing.hero_asset_id;

  return (
    <>
      <Section>
        <Container>
          <Crumb to="/hardware"><IconArrowLeft size={16} /> All hardware</Crumb>
          <HeroGrid>
            <Gallery images={images} heroId={listing.hero_asset_id} alt={listing.display_name || listing.board} />
            <div>
              <Eyebrow>{listing.provider_name} · platform</Eyebrow>
              <Title>{listing.display_name || `${listing.provider_name} ${listing.board}`}</Title>
              {listing.headline && <Headline>{listing.headline}</Headline>}
              <Facts>
                <dt>Board</dt><dd>{listing.board}</dd>
                <dt>Doors</dt><dd>{listing.default_num_doors}</dd>
                <dt>Power</dt><dd>{listing.default_power_type}</dd>
                {listing.variants.length > 0 && <><dt>Products</dt><dd>{listing.variants.length}</dd></>}
                {listing.provider_website && (
                  <><dt>Provider</dt><dd><a href={listing.provider_website} target="_blank" rel="noreferrer">{listing.provider_website.replace(/^https?:\/\//, '')}</a></dd></>
                )}
              </Facts>
              {listing.description && <Paragraphs text={listing.description} />}
            </div>
          </HeroGrid>
        </Container>
      </Section>

      {listing.variants.length > 0 && (
        <Section style={{ paddingTop: 0 }}>
          <Container>
            <H2>Products built on this board</H2>
            <ProductGrid>
              {listing.variants.map((v) => {
                const hero = heroOf(v);
                return (
                  <ProductCard key={v.id} to={publicListingPath(listing.provider_slug, listing.slug, v.slug)}>
                    <div className="thumb">{hero ? <img src={publicFileUrl(hero)} alt={v.name} loading="lazy" /> : <span>{v.sku}</span>}</div>
                    <small>{v.sku}</small>
                    <h3>{v.name}</h3>
                    {v.headline && <p>{v.headline}</p>}
                    <div><Pill tone={v.status}>{VARIANT_STATUS_LABELS[v.status]}</Pill></div>
                  </ProductCard>
                );
              })}
            </ProductGrid>
          </Container>
        </Section>
      )}

      <Specs specs={listing.specs} title="Board specifications" />
      <Documents documents={documents} />
    </>
  );
}

/* ── product page ── */

function ProductPage({ listing, variant }: { listing: PublicListing; variant: PublicVariant }) {
  const own = listing.assets.filter((a) => a.variant_id === variant.id);
  const shared = listing.assets.filter((a) => !a.variant_id);
  const images = own.filter((a) => a.kind === 'image');
  const galleryImages = images.length ? images : shared.filter((a) => a.kind === 'image');
  const documents = [...own.filter((a) => a.kind === 'document'), ...shared.filter((a) => a.kind === 'document')];
  const heroId = variant.hero_asset_id ?? (images.length ? null : listing.hero_asset_id);
  const siblings = listing.variants.filter((v) => v.id !== variant.id);

  return (
    <>
      <Section>
        <Container>
          <Crumb to={publicListingPath(listing.provider_slug, listing.slug)}>
            <IconArrowLeft size={16} /> {listing.display_name || listing.board}
          </Crumb>
          <HeroGrid>
            <Gallery images={galleryImages} heroId={heroId} alt={variant.name} />
            <div>
              <Eyebrow>{listing.provider_name} · {variant.sku}</Eyebrow>
              <Title>{variant.name}</Title>
              {variant.headline && <Headline>{variant.headline}</Headline>}
              <Facts>
                <dt>Availability</dt><dd><Pill tone={variant.status}>{VARIANT_STATUS_LABELS[variant.status]}</Pill></dd>
                <dt>SKU</dt><dd>{variant.sku}</dd>
                <dt>Platform</dt><dd><Link to={publicListingPath(listing.provider_slug, listing.slug)}>{listing.display_name || listing.board}</Link></dd>
                <dt>Doors</dt><dd>{listing.default_num_doors}</dd>
                <dt>Power</dt><dd>{listing.default_power_type}</dd>
              </Facts>
              {variant.description
                ? <Paragraphs text={variant.description} />
                : listing.description ? <Paragraphs text={listing.description} /> : null}
            </div>
          </HeroGrid>
        </Container>
      </Section>

      <Specs specs={[...listing.specs, ...variant.specs]} />
      <Documents documents={documents} />

      {siblings.length > 0 && (
        <Section style={{ paddingTop: 0 }}>
          <Container>
            <H2>Other products on this board</H2>
            <ProductGrid>
              {siblings.map((v) => (
                <ProductCard key={v.id} to={publicListingPath(listing.provider_slug, listing.slug, v.slug)}>
                  <small>{v.sku}</small>
                  <h3>{v.name}</h3>
                  {v.headline && <p>{v.headline}</p>}
                </ProductCard>
              ))}
            </ProductGrid>
          </Container>
        </Section>
      )}
    </>
  );
}

function ListingPage() {
  const { provider = '', board = '', variant: variantSlug } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-listing', provider, board],
    queryFn: () => getPublicListing(provider, board),
    retry: false,
  });
  const variant = data && variantSlug ? data.variants.find((v) => v.slug === variantSlug) : undefined;
  const notFound = isError || (data && variantSlug && !variant);

  return (
    <PublicLayout>
      {isLoading && <Section><Container><Muted>Loading…</Muted></Container></Section>}
      {notFound && (
        <Section>
          <Container>
            <Crumb to="/hardware"><IconArrowLeft size={16} /> All hardware</Crumb>
            <Title>Not found</Title>
            <Muted>There's no published product at this address.</Muted>
          </Container>
        </Section>
      )}
      {data && !notFound && (variant ? <ProductPage listing={data} variant={variant} /> : <BoardPage listing={data} />)}
    </PublicLayout>
  );
}

export default ListingPage;
